import {
  type Actions,
  ImmerReducer,
  createActionCreators,
  createReducerFunction,
} from "immer-reducer";
import {
  anyPass,
  clone,
  findIndex,
  forEachObjIndexed,
  isNil,
  pathSatisfies,
  propEq,
  remove,
} from "ramda";
import type { DefaultRootState } from "react-redux";
import type { ThunkAction } from "redux-thunk";
import type { ISetToolbarSnack } from "actions/context";
import { isRequestFailure, isRequestNotStarted } from "helpers/requests";

import { requestStatusSelector } from "selectors/brand_menu";
import {
  BrandMenuRequestKeys,
  type BrandMenuRequests,
  type IBrandMenuState,
  type IItemRecord,
  type IMenuComponents,
  type IMenuRecord,
  type ISection,
} from "types/brand_menu";
import { RequestStatus } from "types/request";

const EMPTY_REQUEST_STATUS: BrandMenuRequests = {
  [BrandMenuRequestKeys.GetMenus]: RequestStatus.NOT_STARTED,
  [BrandMenuRequestKeys.GetSections]: RequestStatus.NOT_STARTED,
  [BrandMenuRequestKeys.GetMenuComponents]: RequestStatus.NOT_STARTED,
  [BrandMenuRequestKeys.PostItem]: RequestStatus.NOT_STARTED,
  [BrandMenuRequestKeys.PutItem]: RequestStatus.NOT_STARTED,
  [BrandMenuRequestKeys.PostMenu]: RequestStatus.NOT_STARTED,
  [BrandMenuRequestKeys.PutMenu]: RequestStatus.NOT_STARTED,
  [BrandMenuRequestKeys.DeleteMenu]: RequestStatus.NOT_STARTED,
  [BrandMenuRequestKeys.PostOrPutItem]: RequestStatus.NOT_STARTED,
};

export class BrandMenuReducer extends ImmerReducer<IBrandMenuState> {
  public setRequestStatus(
    storeId: string,
    requestKey: BrandMenuRequestKeys,
    status: RequestStatus
  ) {
    if (storeId) {
      if (!this.draftState.requestStatus[storeId]) {
        this.draftState.requestStatus[storeId] = clone(EMPTY_REQUEST_STATUS);
      }
      this.draftState.requestStatus[storeId][requestKey] = status;
    }
  }

  public setRequestStatusForMultilocation(
    storeIds: string[],
    requestKey: BrandMenuRequestKeys,
    status: RequestStatus
  ) {
    storeIds.forEach((storeId) =>
      this.setRequestStatus(storeId, requestKey, status)
    );
  }

  public setMenusForRestaurant(storeId: string, menus: IMenuRecord[]) {
    if (storeId) {
      this.draftState.menus[storeId] = menus;
    }
  }

  public updateMenuForRestaurant(storeId: string, menu: IMenuRecord) {
    if (storeId) {
      const existingMenus = this.draftState.menus[storeId];
      if (existingMenus) {
        const existingIdx = existingMenus.findIndex(propEq("id", menu.id));
        if (existingIdx === -1) {
          existingMenus.push(menu);
        } else {
          existingMenus.splice(existingIdx, 1, menu);
        }
      } else {
        this.draftState.menus[storeId] = [menu];
      }
    }
  }

  public deleteMenuForRestaurant(storeId: string, menu: IMenuRecord) {
    const existingMenus = this.draftState.menus[storeId];
    if (existingMenus) {
      const existingIdx = existingMenus.findIndex(propEq("id", menu.id));
      this.draftState.menus[storeId] = remove(existingIdx, 1, existingMenus);
    }
  }

  public setSectionsForRestaurant(storeId: string, sections: ISection[]) {
    if (storeId) {
      this.draftState.sections[storeId] = sections;
    }
  }

  public setMenuComponentsForRestaurant(
    storeId: string,
    menuComponents: IMenuComponents
  ) {
    if (storeId) {
      forEachObjIndexed((list, key) => {
        if (this.draftState[key]) {
          this.draftState[key][storeId] = list;
        }
      }, menuComponents);
    }
  }

  public setMenuComponentsForMultiLocation(
    keyedComponents: Record<string, IMenuComponents>
  ) {
    forEachObjIndexed(
      (menuComponents, storeId) =>
        this.setMenuComponentsForRestaurant(storeId, menuComponents),
      keyedComponents
    );
  }

  public saveItem(storeId: string, item: IItemRecord) {
    const storeItems = this.draftState.items[storeId];
    if (storeItems) {
      const itemIdx = findIndex(propEq("id", item.id), storeItems);
      if (itemIdx === -1) {
        storeItems.push(item);
      } else {
        storeItems.splice(itemIdx, 1, item);
      }
    } else {
      this.draftState.items[storeId] = [item];
    }
  }
}

const initialState: IBrandMenuState = {
  menus: {},
  sections: {},
  items: {},
  categories: {},
  courses: {},
  modifierGroups: {},
  printerGroups: {},
  taxRates: {},
  requestStatus: {},
};

export default createReducerFunction(BrandMenuReducer, initialState);

export const ActionCreators = createActionCreators(BrandMenuReducer);
export type BrandMenuActions =
  | Actions<typeof BrandMenuReducer>
  | ISetToolbarSnack;

export type ThunkResult<R> = ThunkAction<
  R,
  DefaultRootState,
  undefined,
  BrandMenuActions
>;

export const isFetchNeededForLocation = (
  storeId: string,
  requestKey: BrandMenuRequestKeys,
  getState: () => DefaultRootState
) =>
  pathSatisfies(
    anyPass([isNil, isRequestNotStarted, isRequestFailure]),
    [storeId, requestKey],
    requestStatusSelector(getState())
  );
